/**
 * \u68cb\u76d8\u6e32\u67d3\u5668 \u2014\u2014 \u628a Game \u7684\u72b6\u6001\u753b\u5230 Canvas 2D \u4e0a\u3002
 *
 * \u5c42\u6b21\uff1a\u661f\u91ce\uff08\u9884\u70d8\u7119\uff09\u2192 \u68cb\u76d8\u5e95 \u2192 \u7f51\u683c\u7ebf \u2192 \u68cb\u5b50 \u2192 \u7279\u6b8a\u5757 \u2192 \u9009\u4e2d\u73af \u2192 \u5916\u6846\u63cf\u8fb9\u3002
 */

import { BOARD_SIZE, type Board0, type Coord, type PieceColor, type SpecialType } from '../core/types';
import type { GameSnapshot } from '../core/game';
import { GEM_SPECS, BG, SEMANTIC, getGemSpec } from './palette';
import { drawGem, getSprite, warmupSprites } from './gem';
import { bakeStarfield } from './starfield';
import { silhouette, pathOf, poly } from './geometry';
import type { AnimState } from '../anim/director';

export interface BoardMetrics {
  /** \u6574\u4e2a canvas \u7684 CSS \u5c3a\u5bf8 */
  width: number;
  height: number;
  /** \u68cb\u76d8\u5de6\u4e0a\u89d2 */
  ox: number;
  oy: number;
  /** \u68cb\u76d8\u8fb9\u957f */
  boardPx: number;
  /** \u5355\u683c\u8ddd\uff08cell + gap\uff09 */
  cellPitch: number;
  /** \u5355\u683c\u5185\u5b9e\u9645\u5c3a\u5bf8 */
  cellSize: number;
  dpr: number;
}

export function computeMetrics(cssWidth: number, cssHeight: number, dpr: number): BoardMetrics {
  const pad = Math.round(Math.min(cssWidth, cssHeight) * 0.035);
  const avail = Math.min(cssWidth, cssHeight) - pad * 2;
  const cellPitch = Math.floor(avail / BOARD_SIZE);
  const boardPx = cellPitch * BOARD_SIZE;
  const ox = Math.round((cssWidth - boardPx) / 2);
  const oy = Math.round((cssHeight - boardPx) / 2);
  const gap = Math.max(2, Math.round(cellPitch * 0.07));
  return {
    width: cssWidth,
    height: cssHeight,
    ox, oy, boardPx, cellPitch,
    cellSize: cellPitch - gap,
    dpr,
  };
}

export class BoardRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private starfield: HTMLCanvasElement | null = null;
  private starfieldFor = '';
  /**
   * \u9759\u6001\u5c42\u79bb\u5c4f\u70d8\u7119\uff1a\u68cb\u76d8\u5e95\u886c + \u661f\u5c18 + \u7f51\u683c\u7ebf + \u5916\u6846\u3002
   * \u8fd9\u4e9b\u5185\u5bb9\u6c38\u8fdc\u4e0d\u53d8\uff0c\u4e4b\u524d\u5374\u88ab\u9010\u5e27\u91cd\u753b\uff08\u9759\u6b62\u65f6 90 \u6b21 arc + 1 \u6b21\u6e10\u53d8 + 17 \u6b21\u7f51\u683c\u7ebf\uff09\u3002
   * \u73b0\u5728\u53ea\u5728\u5c3a\u5bf8\u53d8\u5316\u65f6\u91cd\u70d8\u4e00\u6b21\uff0c\u6bcf\u5e27 drawImage \u4e00\u6b21\u5b8c\u4e8b\u3002
   */
  private staticLayer: HTMLCanvasElement | null = null;
  private staticLayerFor = '';
  metrics: BoardMetrics;
  private selection: Coord | null = null;
  private hover: Coord | null = null;
  private dragOffset = { dx: 0, dy: 0 };
  private dragFrom: Coord | null = null;
  private dragOver: Coord | null = null;
  /** \u590d\u7528\u7684\u906e\u853d\u96c6\uff0c\u6bcf\u5e27\u6e05\u7a7a\u91cd\u586b\uff08\u907f\u514d\u9010\u5e27 new Set\uff09 */
  private hiddenScratch = new Set<string>();

  constructor(canvas: HTMLCanvasElement, metrics: BoardMetrics) {
    this.canvas = canvas;
    this.metrics = metrics;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D \u4e0a\u4e0b\u6587\u4e0d\u53ef\u7528');
    this.ctx = ctx;
    this.applySize(metrics);
  }

  applySize(metrics: BoardMetrics): void {
    this.metrics = metrics;
    const { width, height, dpr } = metrics;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    warmupSprites(metrics.cellSize, dpr);
    // \u661f\u91ce\u53ea\u70d8\u7119\u4e00\u6b21\uff08\u5c3a\u5bf8\u4e0d\u53d8\u5219\u590d\u7528\uff09
    const key = `${Math.round(width)}x${Math.round(height)}`;
    if (key !== this.starfieldFor) {
      const isDesktop = Math.min(width, height) > 520;
      this.starfield = bakeStarfield({
        width: Math.round(width),
        height: Math.round(height),
        count: isDesktop ? 260 : 110,
      });
      this.starfieldFor = key;
    }
  }

  /** \u70d8\u7119\u9759\u6001\u5c42\uff08\u76d8\u9762\u4e0d\u53d8\u65f6\u53ea\u505a\u4e00\u6b21\uff09 */
  private ensureStaticLayer(): void {
    const m = this.metrics;
    const key = `${m.width}x${m.height}x${m.boardPx}x${m.dpr}`;
    if (this.staticLayer && key === this.staticLayerFor) return;

    const layer = document.createElement('canvas');
    layer.width = Math.round(m.width * m.dpr);
    layer.height = Math.round(m.height * m.dpr);
    const lctx = layer.getContext('2d');
    if (!lctx) return;
    lctx.setTransform(m.dpr, 0, 0, m.dpr, 0, 0);

    // \u661f\u91ce\u80cc\u666f
    if (this.starfield) {
      lctx.drawImage(this.starfield, 0, 0, m.width, m.height);
    } else {
      lctx.fillStyle = BG.base;
      lctx.fillRect(0, 0, m.width, m.height);
    }
    // \u68cb\u76d8\u5e95\u886c + \u661f\u5c18 + \u7f51\u683c\u7ebf + \u5916\u6846
    this.paintBoardBase(lctx, m);
    this.paintGrid(lctx, m);
    this.paintBoardStroke(lctx, m);

    this.staticLayer = layer;
    this.staticLayerFor = key;
  }

  setSelection(c: Coord | null): void { this.selection = c; }

  /** 悬停格高亮（仅鼠标；传 null 清除） */
  setHover(c: Coord | null): void { this.hover = c; }

  setDrag(from: Coord | null, dx: number, dy: number, over: Coord | null = null): void {
    this.dragFrom = from;
    this.dragOffset = { dx, dy };
    this.dragOver = over;
  }

  /** \u5355\u683c\u4e2d\u5fc3\uff08CSS \u5750\u6807\uff09 */
  cellCenter(c: Coord): { x: number; y: number } {
    const m = this.metrics;
    const step = m.cellPitch;
    const gap = step - m.cellSize;
    return {
      x: m.ox + c.c * step + gap / 2 + m.cellSize / 2,
      y: m.oy + c.r * step + gap / 2 + m.cellSize / 2,
    };
  }

  render(game: GameSnapshot, anim?: AnimState): void {
    const ctx = this.ctx;
    const m = this.metrics;
    ctx.clearRect(0, 0, m.width, m.height);

    // \u6bcf\u5e27\u540c\u6b65\u52a8\u6001\u906e\u853d\u96c6\uff08\u4e0b\u843d\u52a8\u4f5c\u81ea\u5df1\u7ef4\u62a4\uff0c\u843d\u5b9a\u5373\u91ca\u653e\uff09
    if (anim) {
      this.animHiddenCells = anim.hiddenTargets;
    } else {
      this.animHiddenCells = EMPTY_SET;
    }

    // \u89c6\u89c9\u68cb\u76d8\u4f18\u5148\uff1a\u52a8\u753b\u671f\u95f4\u753b\u300c\u65f6\u95f4\u8f74\u6b64\u523b\u7684\u76d8\u9762\u300d\u800c\u975e\u7ec8\u5c40
    const board = anim?.visualBoard ?? game.board;

    // 静态层一次 drawImage（取代原本的 90 次 arc + 渐变 + 17 次网格线描边）
    this.ensureStaticLayer();
    if (this.staticLayer) {
      ctx.drawImage(this.staticLayer, 0, 0, m.width, m.height);
    } else {
      this.drawStarfieldArea();
    }

    // 本帧的遮挡集只构建一次，供棋子层与特殊块层共用
    const hidden = this.buildHiddenSet(anim);

    this.drawHoverHighlight();
    this.drawDragTargetHighlight();
    this.drawPieces(board, anim, hidden);
    this.drawSpecials(board, hidden);
    this.drawSelectionRing();
    // 最后画动画层（带抬起阴影），盖在所有静态层之上
    if (anim) this.drawAnimLayer(anim);
  }

  /** \u52a8\u753b\u5c42\uff1a\u4e0b\u843d\u4e2d\u7684\u68cb\u5b50\uff08\u6700\u4e0a\u5c42\uff0c\u5e26\u9634\u5f71\uff09 */
  private drawAnimLayer(anim: AnimState): void {
    const ctx = this.ctx;
    const m = this.metrics;

    // \u4e0b\u843d\u4e2d\u7684\u68cb\u5b50
    for (const f of anim.falling) {
      ctx.save();
      ctx.globalAlpha = f.alpha;
      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = m.cellSize * 0.22;
      ctx.shadowOffsetY = m.cellSize * 0.05;
      this.drawGemFast(f.color, f.x, f.y, 1);
      ctx.restore();
    }

    // \u6b63\u5728\u5f39\u5165\u7684\u7279\u6b8a\u5757\uff08\u653e\u5927\u540e\u56de\u7f29\uff09\uff1a\u52a8\u753b\u5c42\u81ea\u5df1\u753b\u7f29\u653e\u7684\u7279\u6b8a\u5757
    for (const s of anim.spawning) {
      const center = this.cellCenter(s.cell);
      const scale = 0.4 + s.progress * 0.6;
      ctx.save();
      ctx.globalAlpha = Math.min(1, s.progress * 2.2);
      drawSpecialBlock(ctx, s.special, center.x, center.y, m.cellSize * 0.44 * scale, s.color);
      ctx.restore();
      this.drawSpawnHighlight(center.x, center.y, m.cellSize * 0.5 * scale, s.progress);
    }
  }

  private drawSpawnHighlight(cx: number, cy: number, r: number, progress: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.5);
    g.addColorStop(0, `rgba(180,220,255,${0.5 * (1 - progress)})`);
    g.addColorStop(1, 'rgba(180,220,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** \u60ac\u505c\u683c\u5fae\u9ad8\u4eae\uff08\u67d4\u548c\u767d\u5149\u5708\uff0c\u4e0d\u4e0e\u9009\u4e2d\u73af\u62a2\u6ce8\u610f\u529b\uff09 */
  private drawHoverHighlight(): void {
    // \u62d6\u62fd\u6216\u6309\u4e0b\u671f\u95f4\u4e0d\u663e\u793a\uff0c\u907f\u514d\u4e0e\u76ee\u6807\u683c\u9ad8\u4eae\u6253\u67b6
    if (!this.hover || this.dragFrom) return;
    const ctx = this.ctx;
    const m = this.metrics;
    const center = this.cellCenter(this.hover);
    const rr = m.cellSize * 0.50;

    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.30)';
    ctx.lineWidth = Math.max(1.5, m.cellSize * 0.030);
    ctx.beginPath();
    ctx.arc(center.x, center.y, rr, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  /** \u62d6\u62fd\u4e2d\u7684\u76ee\u6807\u683c\u9ad8\u4eae\uff08\u9752\u84dd\u865a\u6846\uff09 */
  private drawDragTargetHighlight(): void {
    if (!this.dragFrom || !this.dragOver) return;
    const over = this.dragOver;
    if (over.c === this.dragFrom.c && over.r === this.dragFrom.r) return;
    const ctx = this.ctx;
    const m = this.metrics;
    const center = this.cellCenter(over);
    const half = m.cellSize * 0.52;

    ctx.save();
    roundRect(ctx, center.x - half, center.y - half, half * 2, half * 2, m.cellSize * 0.2);
    ctx.fillStyle = 'rgba(127,216,255,0.14)';
    ctx.fill();
    ctx.strokeStyle = SEMANTIC.selectionRing;
    ctx.lineWidth = Math.max(1.5, m.cellSize * 0.035);
    ctx.setLineDash([m.cellSize * 0.12, m.cellSize * 0.09]);
    ctx.stroke();
    ctx.restore();
  }

  private drawStarfieldArea(): void {
    const ctx = this.ctx;
    const m = this.metrics;
    if (this.starfield) {
      ctx.drawImage(this.starfield, 0, 0, m.width, m.height);
    } else {
      ctx.fillStyle = BG.base;
      ctx.fillRect(0, 0, m.width, m.height);
    }
  }

  /** \u68cb\u76d8\u5e95\u886c\uff08\u70d8\u7119\u5230\u9759\u6001\u5c42\uff09 */
  private paintBoardBase(ctx: CanvasRenderingContext2D, m: BoardMetrics): void {
    const radius = Math.round(m.boardPx * 0.045);
    roundRect(ctx, m.ox, m.oy, m.boardPx, m.boardPx, radius);
    ctx.fillStyle = 'rgba(11,16,32,0.82)';
    ctx.fill();

    // \u5e95\u886c\u5fc3\uff08\u68cb\u76d8\u5185\u90e8\u7684\u7d2b\u8272\u5149\u6655\uff09
    ctx.save();
    roundRect(ctx, m.ox, m.oy, m.boardPx, m.boardPx, radius);
    ctx.clip();
    const g = ctx.createRadialGradient(
      m.ox + m.boardPx * 0.35, m.oy + m.boardPx * 0.28, 0,
      m.ox + m.boardPx * 0.35, m.oy + m.boardPx * 0.28, m.boardPx * 0.85,
    );
    g.addColorStop(0, 'rgba(58,46,110,0.55)');
    g.addColorStop(0.5, 'rgba(34,48,94,0.28)');
    g.addColorStop(1, 'rgba(11,16,32,0)');
    ctx.fillStyle = g;
    ctx.fillRect(m.ox, m.oy, m.boardPx, m.boardPx);

    // \u661f\u5c18\u7f13\u5e95\uff08\u68cb\u76d8\u5185\u90e8\u5c11\u91cf\u661f\u70b9\uff0c\u907f\u514d\u7eaf\u9ed1\uff09
    ctx.globalAlpha = 0.28;
    const rand = mulberry(20261012);
    ctx.fillStyle = BG.stardust;
    for (let i = 0; i < 90; i++) {
      const x = m.ox + rand() * m.boardPx;
      const y = m.oy + rand() * m.boardPx;
      const rr = 0.5 + rand() * 1.1;
      ctx.beginPath();
      ctx.arc(x, y, rr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private paintGrid(ctx: CanvasRenderingContext2D, m: BoardMetrics): void {
    ctx.save();
    roundRect(ctx, m.ox, m.oy, m.boardPx, m.boardPx, Math.round(m.boardPx * 0.045));
    ctx.clip();
    ctx.strokeStyle = 'rgba(42,53,82,0.5)';
    ctx.lineWidth = 1;
    // \u6574\u6761\u7f51\u683c\u7528\u5355\u4e2a path \u4e00\u6b21 stroke\uff08\u539f\u672c\u6bcf\u6761\u7ebf\u4e00\u6b21 beginPath+stroke\uff09
    ctx.beginPath();
    for (let i = 1; i < BOARD_SIZE; i++) {
      const p = m.ox + i * m.cellPitch;
      ctx.moveTo(p, m.oy); ctx.lineTo(p, m.oy + m.boardPx);
      const q = m.oy + i * m.cellPitch;
      ctx.moveTo(m.ox, q); ctx.lineTo(m.ox + m.boardPx, q);
    }
    ctx.stroke();
    ctx.restore();
  }

  private drawPieces(board: Board0, anim: AnimState | undefined, hidden: Set<string>): void {
    // 遇到动画的格子需要跳过（由动画层接管）

    // 第一轮：非拖拽、非动画的棋子
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (this.dragFrom && this.dragFrom.c === col && this.dragFrom.r === row) continue;
        if (hidden.has(`${col},${row}`)) continue;
        this.paintPiece(board, col, row, 0, 0, false);
      }
    }

    // \u7b2c\u4e8c\u8f6e\uff1a\u88ab\u62d6\u62fd\u7684\u68cb\u5b50\u753b\u5728\u6700\u4e0a\u5c42\uff0c\u5b8c\u5168\u8ddf\u624b
    if (this.dragFrom) {
      const { c, r } = this.dragFrom;
      this.paintPiece(board, c, r, this.dragOffset.dx, this.dragOffset.dy, true);
    }

    // \u7b2c\u4e09\u8f6e\uff1a\u6b63\u5728\u4ea4\u6362\u7684\u4e24\u9897\u68cb\u5b50
    if (anim?.swap) this.drawSwapPair(anim.swap);

    // \u7b2c\u56db\u8f6e\uff1a\u6b63\u5728\u788e\u88c2\u7684\u68cb\u5b50
    for (const s of anim?.shattering ?? []) this.drawShatter(s);
  }

  /** \u52a8\u753b\u5c42\u6bcf\u5e27\u62a5\u544a\u7684\u9700\u906e\u853d\u683c\uff08\u7531 AnimState.hiddenTargets \u9a71\u52a8\uff0c\u52a8\u6001\u589e\u51cf\uff09 */
  private animHiddenCells: Set<string> = EMPTY_SET;

  /** \u6784\u5efa\u3010\u4e0d\u5e94\u7531\u9759\u6001\u5c42\u7ed8\u5236\u3011\u7684\u683c\u5b50\u96c6\u5408\uff08\u590d\u7528\u5b9e\u4f8b\uff0c\u6bcf\u5e27\u6e05\u7a7a\u91cd\u586b\uff09 */
  private buildHiddenSet(anim?: AnimState): Set<string> {
    const hidden = this.hiddenScratch;
    hidden.clear();
    for (const k of this.animHiddenCells) hidden.add(k);
    if (!anim) return hidden;
    for (const s of anim.shattering) hidden.add(`${s.cell.c},${s.cell.r}`);
    if (anim.swap) {
      hidden.add(`${anim.swap.from.c},${anim.swap.from.r}`);
      hidden.add(`${anim.swap.to.c},${anim.swap.to.r}`);
    }
    return hidden;
  }

  /**
   * 绘制交换中的两颗棋子（使用交换前颜色，A 从 from 滑向 to，B 反向）。
   * 视觉棋盘已在动作启动时完成换位并遮蔽两格，这里只负责滑动渲染。
   */
  private drawSwapPair(swap: NonNullable<AnimState['swap']>): void {
    const { from, to, progress, aColor, bColor } = swap;
    const aCenter = this.cellCenter(from);
    const bCenter = this.cellCenter(to);

    // A（原在 from）滑向 to
    const ax = aCenter.x + (bCenter.x - aCenter.x) * progress;
    const ay = aCenter.y + (bCenter.y - aCenter.y) * progress;
    this.drawGemFast(aColor, ax, ay, 1);

    // B（原在 to）滑向 from
    const bx = bCenter.x + (aCenter.x - bCenter.x) * progress;
    const by = bCenter.y + (aCenter.y - bCenter.y) * progress;
    this.drawGemFast(bColor, bx, by, 1);
  }

  /**
   * \u5feb\u901f\u7ed8\u5236\u4e00\u9897\u68cb\u5b50\uff1a\u4f18\u5148\u7528\u9884\u70d8\u7119\u8d34\u56fe\uff08\u5355\u6b21 drawImage\uff09\uff0c
   * \u907f\u514d\u8d70 drawGem \u4e03\u6b65\u6cd5\uff08\u7ea6 40 \u6b21\u8def\u5f84\u64cd\u4f5c\uff09\u3002
   * \u52a8\u753b\u5c42\u6bcf\u5e27\u53ef\u80fd\u8981\u753b\u5341\u51e0\u9897\uff0c\u8fd9\u91cc\u662f\u4e3b\u8981\u4f18\u5316\u70b9\u3002
   */
  private drawGemFast(color: string, cx: number, cy: number, scale: number): void {
    const key = color as PieceColor;
    const m = this.metrics;
    const sprite = GEM_SPECS[key] ? getSprite(key, m.cellSize, m.dpr) : null;
    if (sprite) {
      const w = m.cellSize * scale;
      this.ctx.drawImage(sprite, cx - w / 2, cy - w / 2, w, w);
      return;
    }
    drawGemByColor(this.ctx, color, cx, cy, m.cellSize * 0.44 * scale);
  }

  /**
   * \u7ed8\u5236\u788e\u88c2\u4e2d\u7684\u68cb\u5b50\uff08S7 \u6bcd\u9898\uff1a\u73bb\u7483\u788e\u88c2\uff0c\u788e\u6210\u7247\u72b6\u800c\u975e\u5706\u7c92\u5b50\uff09\u3002
   * \u968f progress\uff1a\u672c\u4f53\u7f29\u5c0f\u3001\u900f\u660e\u3001\u788e\u7247\u5411\u5916\u98de\u6563\u3002
   */
  private drawShatter(s: { cell: Coord; color: string; progress: number }): void {
    const ctx = this.ctx;
    const m = this.metrics;
    const center = this.cellCenter(s.cell);
    const t = s.progress;
    const r = m.cellSize * 0.44;

    ctx.save();

    // \u805a\u7126\u95ea\u5149\uff08\u524d 30%\uff09
    if (t < 0.35) {
      const flash = 1 - t / 0.35;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const g = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, r * 1.6);
      g.addColorStop(0, hexA(s.color, 0.55 * flash));
      g.addColorStop(0.55, hexA(s.color, 0.22 * flash));
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(center.x, center.y, r * 1.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // \u672c\u4f53\uff1a\u968f progress \u7f29\u5c0f\u5e76\u900f\u660e\uff08drawGemFast \u7684 scale \u4ee5 cellSize \u4e3a\u57fa\u51c6\uff0c0.44 = \u539f\u534a\u5f84\uff09
    const bodyScale = 1 - t * 0.55;
    const bodyAlpha = Math.max(0, 1 - t * 1.6);
    if (bodyAlpha > 0.02) {
      ctx.save();
      ctx.globalAlpha = bodyAlpha;
      this.drawGemFast(s.color, center.x, center.y, 0.44 * bodyScale);
      ctx.restore();
    }

    // \u788e\u7247\uff1a8 \u7247\u591a\u8fb9\u5f62\u77ed\u8fb9\uff0c\u5411\u5916\u98de\u6563 + \u65cb\u8f6c
    const shardCount = 8;
    const spread = r * (0.6 + t * 2.4);
    const shardAlpha = Math.max(0, 1 - t * 1.15);
    if (shardAlpha > 0.02) {
      ctx.save();
      ctx.globalAlpha = shardAlpha;
      for (let i = 0; i < shardCount; i++) {
        const baseAngle = (i * Math.PI * 2) / shardCount + 0.4;
        const cx = center.x + Math.cos(baseAngle) * spread;
        const cy = center.y + Math.sin(baseAngle) * spread;
        const size = r * (0.30 - t * 0.14);
        if (size <= 0.5) continue;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(baseAngle + t * 2.2);
        // \u4e09\u89d2/\u56db\u89d2\u5f62\u7247\uff08\u73bb\u7483\u788e\u7247\uff0c\u975e\u5706\u5f62\uff09
        const pts = poly(0, 0, size, 3 + (i % 2), Math.PI / 6);
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x, pts[k].y);
        ctx.closePath();
        ctx.fillStyle = hexA(s.color, 0.7);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.55)';
        ctx.lineWidth = Math.max(0.6, r * 0.03);
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();
    }

    ctx.restore();
  }

  /** \u7ed8\u5236\u5355\u9897\u68cb\u5b50\uff1bdragging=true \u65f6\u5e26\u62ac\u8d77\u9634\u5f71\u4e0e\u653e\u5927 */
  private paintPiece(
    board: Board0,
    col: number, row: number,
    offX: number, offY: number,
    dragging: boolean,
  ): void {
    const ctx = this.ctx;
    const m = this.metrics;
    const cell = board.cells[row]?.[col];
    if (!cell || cell.kind !== 'PIECE' || !cell.color) return;

    const center = this.cellCenter({ c: col, r: row });
    const x = center.x + offX;
    const y = center.y + offY;

    if (dragging) {
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.55)';
      ctx.shadowBlur = m.cellSize * 0.42;
      ctx.shadowOffsetY = m.cellSize * 0.10;
    }

    const scale = dragging ? 1.12 : 1;
    const sprite = getSprite(cell.color as PieceColor, m.cellSize, m.dpr);
    if (sprite) {
      const w = m.cellSize * scale;
      ctx.drawImage(sprite, x - w / 2, y - w / 2, w, w);
    } else {
      drawGem(ctx, cell.color as PieceColor, {
        cx: x, cy: y,
        r: m.cellSize * 0.44 * scale,
        alpha: dragging ? 1 : 0.92,
      });
    }

    if (dragging) ctx.restore();
  }

  private drawSpecials(board: Board0, hidden: Set<string>): void {
    const ctx = this.ctx;
    const m = this.metrics;
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (hidden.has(`${col},${row}`)) continue;
        const cell = board.cells[row]?.[col];
        if (!cell || cell.kind !== 'SPECIAL' || !cell.special) continue;
        const center = this.cellCenter({ c: col, r: row });
        drawSpecialBlock(ctx, cell.special, center.x, center.y, m.cellSize * 0.44, cell.color);
      }
    }
  }

  private drawSelectionRing(): void {
    if (!this.selection) return;
    const ctx = this.ctx;
    const m = this.metrics;
    const center = this.cellCenter(this.selection);
    const rr = m.cellSize * 0.52;

    ctx.save();
    // \u767d\u9ad8\u5149 + \u9752\u84dd\u5916\u73af\uff08S8\uff1a\u4e25\u7981\u91d1\u8272\uff09
    ctx.strokeStyle = SEMANTIC.selectionRing;
    ctx.lineWidth = Math.max(2, m.cellSize * 0.045);
    ctx.shadowColor = SEMANTIC.selectionRing;
    ctx.shadowBlur = m.cellSize * 0.28;
    ctx.beginPath();
    ctx.arc(center.x, center.y, rr, 0, Math.PI * 2);
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = Math.max(1, m.cellSize * 0.022);
    ctx.beginPath();
    ctx.arc(center.x, center.y, rr - m.cellSize * 0.05, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private paintBoardStroke(ctx: CanvasRenderingContext2D, m: BoardMetrics): void {
    roundRect(ctx, m.ox, m.oy, m.boardPx, m.boardPx, Math.round(m.boardPx * 0.045));
    ctx.strokeStyle = BG.boardStroke;
    ctx.lineWidth = Math.max(1.5, m.boardPx * 0.004);
    ctx.stroke();
  }
}

/** \u5171\u7528\u7a7a\u96c6\uff08\u907f\u514d\u9010\u5e27 new Set\uff09 */
const EMPTY_SET: Set<string> = new Set();

// \u2500\u2500\u2500\u2500 \u7279\u6b8a\u5757\u4e0e\u969c\u788d \u2500\u2500\u2500\u2500

function drawSpecialBlock(
  ctx: CanvasRenderingContext2D,
  type: SpecialType,
  cx: number, cy: number, r: number,
  color?: PieceColor,
): void {
  const base = color ? getGemSpec(color) : getGemSpec('neptune');
  ctx.save();

  // \u57fa\u5e95\uff1a\u540c\u8272\u73bb\u7483\u4f53
  drawGem(ctx, color ?? 'neptune', { cx, cy, r: r * 0.92 });

  switch (type) {
    case 'THRUSTER': {
      // \u63a8\u8fdb\u5668\uff1a\u53cc\u5411\u7bad\u5934
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = 'rgba(255,255,255,0.92)';
      ctx.lineWidth = Math.max(2, r * 0.13);
      ctx.lineCap = 'round';
      for (const s of [1, -1]) {
        ctx.beginPath();
        ctx.moveTo(cx - r * 0.42 * s, cy);
        ctx.lineTo(cx + r * 0.42 * s, cy);
        ctx.stroke();
      }
      // \u7bad\u5934
      for (const s of [1, -1]) {
        ctx.beginPath();
        ctx.moveTo(cx + r * 0.60 * s, cy);
        ctx.lineTo(cx + r * 0.28 * s, cy - r * 0.30);
        ctx.lineTo(cx + r * 0.28 * s, cy + r * 0.30);
        ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.fill();
      }
      ctx.restore();
      break;
    }
    case 'METEOR_BURST': {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = 'rgba(255,180,120,0.95)';
      ctx.lineWidth = Math.max(1.5, r * 0.10);
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI * 2) / 8;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r * 0.34, cy + Math.sin(a) * r * 0.34);
        ctx.lineTo(cx + Math.cos(a) * r * 0.9, cy + Math.sin(a) * r * 0.9);
        ctx.stroke();
      }
      ctx.restore();
      break;
    }
    case 'BLACK_HOLE': {
      ctx.save();
      const g = ctx.createRadialGradient(cx, cy, r * 0.1, cx, cy, r * 0.8);
      g.addColorStop(0, 'rgba(0,0,0,0.95)');
      g.addColorStop(0.55, 'rgba(20,10,40,0.85)');
      g.addColorStop(1, 'rgba(90,60,180,0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.8, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(180,150,255,0.85)';
      ctx.lineWidth = Math.max(1.5, r * 0.075);
      ctx.beginPath(); ctx.ellipse(cx, cy, r * 0.92, r * 0.34, 0.4, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
      break;
    }
    case 'NEBULA_CORE': {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 0.95);
      g.addColorStop(0, hexA(base.core, 0.9));
      g.addColorStop(0.4, 'rgba(160,120,255,0.45)');
      g.addColorStop(1, 'rgba(80,50,180,0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.95, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      break;
    }
  }
  ctx.restore();
}

// \u2500\u2500\u2500\u2500 \u5c0f\u5de5\u5177 \u2500\u2500\u2500\u2500

/** \u6309\u5b9e\u9645\u989c\u8272\u4ee3\u53f7\u7ed8\u5236\u68cb\u5b50\uff08\u4f9b\u52a8\u753b\u5c42\u7528\uff09 */
function drawGemByColor(
  ctx: CanvasRenderingContext2D,
  color: string,
  cx: number, cy: number, r: number,
): void {
  const key = color as PieceColor;
  if (GEM_SPECS[key]) {
    drawGem(ctx, key, { cx, cy, r });
    return;
  }
  // \u517c\u5bb9\u76f4\u63a5\u4f20\u5165 hex\uff08\u4fdd\u5e95\uff09
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function hexA(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// \u672a\u4f7f\u7528\u5bfc\u5165\u5360\u4f4d\uff08\u4fdd\u7559\u4f9b P4 \u62d3\u5c55\uff09
void silhouette; void pathOf;
